import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useNavigate } from 'react-router';
import { useMemo } from 'react';
import { routePathsOrderPickup } from '../routePaths/pickupsRoutePaths';
import { routePathsAdmin } from '../routePaths/adminRoutePaths';
import { type SubLinkProps } from '../HeaderMenu/types';
import { useCountries } from './useCountries';
import { useBattery } from './useBattery';
import { useCompanies } from './useCompanies';
import { useFlag } from './useFlag';
import { useRnpAccess } from './useRnpAccess';

const isCurrentRoute = (path: string, currentPath: string, exactMatch?: boolean) => {
  const pathToMatch = exactMatch ? path : `${path}/*`;

  return !!matchPath(pathToMatch, currentPath);
};

export const usePickupSublinks = () => {
  const {
    supportedCountries: { sweden, denmark, norway },
  } = useCountries();

  const dkPickupScheduleFlag = useFlag('dk-pickup-schedule');
  const sortingGuidanceSeFlag = useFlag('sorting-guidance-se');
  const sortingGuidanceDkFlag = useFlag('sorting-guidance-dk');
  const rnpNewAccessRoleFlag = useFlag('rnp-new-access-role');
  const hasRnpAccess = useRnpAccess();

  const {
    supportedCompanies: { recycling, confidential },
  } = useCompanies();

  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation();

  const { batteryEnabled } = useBattery();

  const subLinks = useMemo(() => {
    const links: SubLinkProps[] = [
      {
        text: t('routes.order-pickup'),
        onClick: async (event: React.MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();

          await navigate(routePathsOrderPickup.ORDER_PICKUP);
        },
        active:
          isCurrentRoute(routePathsOrderPickup.ORDER_PICKUP, location.pathname, true) ||
          isCurrentRoute(routePathsOrderPickup.CONFIRMATION, location.pathname) ||
          isCurrentRoute(routePathsOrderPickup.CONFIRMATION_ID, location.pathname),
      },
    ];

    if (batteryEnabled) {
      links.push({
        text: t('routes.battery-pickup'),
        onClick: async (event: React.MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();
          await navigate(routePathsOrderPickup.LI_ION_BATTERIES);
        },
        active: isCurrentRoute(routePathsOrderPickup.LI_ION_BATTERIES, location.pathname),
      });
    }

    links.push({
      text: t('routes.previous-orders'),
      onClick: async (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        await navigate(routePathsOrderPickup.PREVIOUS_ORDERS);
      },
      active: isCurrentRoute(routePathsOrderPickup.PREVIOUS_ORDERS, location.pathname),
    });

    if (recycling.countries.denmark) {
      links.push({
        text: t('routes.declaration'),
        onClick: async (event: React.MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();
          await navigate(routePathsOrderPickup.DECLARATION);
        },
        active: isCurrentRoute(routePathsOrderPickup.DECLARATION, location.pathname),
      });
    }

    if (
      sweden ||
      norway ||
      confidential.hasAccess ||
      (dkPickupScheduleFlag && recycling.countries.denmark)
    ) {
      links.push({
        text: t('routes.pickup-schedule'),
        onClick: async (event: React.MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();
          await navigate(routePathsOrderPickup.PICKUP_SCHEDULE);
        },
        active: isCurrentRoute(routePathsOrderPickup.PICKUP_SCHEDULE, location.pathname),
      });
    }

    if (sweden || norway || recycling.countries.denmark || confidential.hasAccess) {
      links.push({
        text: t('routes.rented-containers'),
        onClick: async (event: React.MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();
          await navigate(routePathsOrderPickup.RENTED_CONTAINERS);
        },
        active: isCurrentRoute(routePathsOrderPickup.RENTED_CONTAINERS, location.pathname),
      });
    }

    if (rnpNewAccessRoleFlag && hasRnpAccess) {
      links.push({
        text: t('request-new-location.menu.title'),
        onClick: async (event: React.MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();
          await navigate(routePathsAdmin.REQUEST_NEW_LOCATION);
        },
        active: isCurrentRoute(routePathsAdmin.REQUEST_NEW_LOCATION, location.pathname),
      });
    }

    if ((sortingGuidanceSeFlag && sweden) || (sortingGuidanceDkFlag && denmark)) {
      links.push({
        text: t('routes.sorting-guidance'),
        onClick: async (event: React.MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();
          await navigate(routePathsOrderPickup.SORTING_GUIDANCE);
        },
        active: isCurrentRoute(routePathsOrderPickup.SORTING_GUIDANCE, location.pathname),
      });
    }

    return links;
  }, [
    t,
    location.pathname,
    batteryEnabled,
    denmark,
    sortingGuidanceSeFlag,
    sortingGuidanceDkFlag,
    recycling.countries.denmark,
    sweden,
    norway,
    confidential.hasAccess,
    dkPickupScheduleFlag,
    rnpNewAccessRoleFlag,
    hasRnpAccess,
    navigate,
  ]);

  return subLinks;
};
